<script lang="ts" setup>
	import { useDebounceFn } from '@vueuse/core'
	import type { EligibilityResponse, EligibilityType } from './eligibility-dialog.vue'
	import usePurchases from '~/services/apis/purchases-info'
	import { useProductsApi, type GroupProductByNameContext, type ProductContext } from '~/services/apis/products'
	import usePurchaseInfo from '~/composables/usePurchaseInfo'

	// NOTE: This page is only available for health and auto plans since they are the only plans that can be migrated
	const props = defineProps<{
		planDetails: PlanInformationContext | null
		planId: string
	}>()
	export type MigrationRoute = {
		type: 'upgrade' | 'downgrade'
		plan_id: string
		product_id: string
		category: string
		page: string
		coverPeriod: number | string
		isMigrationPolicies: boolean
	}
	const { addProductsIconFill, toLower, urlQuery } = useUtils()
	const { computePlanDowngrade, computePlanUpgrade } = usePurchases()
	const { currencyToNaira } = useCurrency()
	const activeTab = shallowRef('')
	const newProductName = shallowRef('')
	const migrationDisabled = shallowRef(false)
	const migrationMethod = shallowRef('')
	const migrationDialog = shallowRef(false)
	const comparePlanId = shallowRef('')
	const loadingComparison = shallowRef(false)
	const computingGrades = shallowRef(false)
	const eligibilityDialog = shallowRef(false)
	const eligibilityDialogType = shallowRef<EligibilityType>('new-user')
	// Plan Information
	const {
		categoryType,
		categoryName: planCategory,
		planName: currentPlanName,
		coverPeriod,
		planInformation: planInfo
	} = usePurchaseInfo({ planInformation: computed(() => props.planDetails) })
	const isMigratingPolicy = computed(() => urlQuery.value?.migrationType === 'policies')
	const comparisonPrice = shallowRef<ProductPriceResponse>({
		is_dynamic_price: false,
		id: '',
		genius_price: 0,
		month: ''
	})
	const currentPlanPriceDetails = shallowRef<ProductPriceResponse>({
		genius_price: 0,
		month: ''
	})
	const gradeStatus = computed(() => {
		// Plans with dynamic price set to true is subscription plan and  therefore has a higher precidence
		return currentPlanPriceDetails.value?.is_dynamic_price ||
			(Math.round(currentPlanPriceDetails.value.genius_price) > Math.round(comparisonPrice.value.genius_price) &&
				!comparisonPrice.value.is_dynamic_price)
			? 'downgrade'
			: comparisonPrice.value.is_dynamic_price ||
				  Math.round(currentPlanPriceDetails.value.genius_price) <
						Math.round(comparisonPrice.value.genius_price)
				? 'upgrade'
				: ''
	})
	const { getProductPriceByMonth } = useProductsApi()
	const getProductPrice = useDebounceFn(async (product_id: string, plan?: ProductContext[number]) => {
		comparisonPrice.value.genius_price = 0
		comparisonPrice.value.is_dynamic_price = false
		if (product_id) {
			resolve(async () => {
				const res = await getProductPriceByMonth(product_id, coverPeriod.value)
				if (res?.data) {
					comparisonPrice.value = { ...res.data, genius_price: Math.round(res.data.market_price as number) }
					if (plan?.name && !planInfo.value?.can_migrate && newProductName.value !== plan.name) {
						newProductName.value = plan.name
					}
				}
			}, computingGrades)
		}
	}, 100)
	const eligibilityDialogRef = useTemplateRef('eligibilityDialogRef')

	const { hasPolicyToMigrate, updateHasData, totalRecords: selectedPolicyToMigrate } = useMigration()

	// Fetch plan list
	const { getProductList } = useProductsApi()
	const primaryPlanList = ref<lineTabContext[]>([])
	const secondaryPlanList = ref<(lineTabContext & ProductContext[number])[]>([])
	const groupedProduct = ref<GroupProductByNameContext | null>(null)
	const secondaryPlanSelected = shallowRef(0)
	const pillTabTransform = (arg: Array<lineTabContext | ProductContext[number]>) => {
		return arg.map(item => {
			return {
				...item,
				key: toLower(item.code_name),
				name: toLower(item.name).replace('care', 'Care')
			}
		}) as (lineTabContext & ProductContext[number])[]
	}
	const productHighlight = computed(() => {
		if (categoryType.value.isHealth) {
			return secondaryPlanList.value.find(item => String(item.key) === String(secondaryPlanSelected.value))
		}
		return secondaryPlanList.value.find(
			item => String(item.key) === String(activeTab.value) || String(item.name) === String(activeTab.value)
		)
	})
	const navigateToMigration = async (productId?: string) => {
		const encoded = encryptData({
			plan_id: planInfo.value?.id ?? '',
			product_id: productId ?? productHighlight.value?.id ?? '',
			type: gradeStatus.value,
			coverPeriod: coverPeriod.value,
			category: planCategory.value,
			isMigrationPolicies: isMigratingPolicy.value
		} as MigrationRoute)

		await navigateTo({
			path: `/mycover/${props.planId}/plan-migration`,
			query: {
				info: encoded
			}
		})
	}
	const confirmMigration = async (productId?: string) => {
		let id = String(productHighlight.value?.id ?? '')
		if (!isNaN(Number(productId)) || typeof productId === 'string') {
			id = String(productId)
		}
		eligibilityDialog.value = false
		resolve(async () => {
			if (planInfo.value?.is_expired) {
				eligibilityDialogType.value = 'expired-plan'
				eligibilityDialog.value = true
				return null
			}
			let result: EligibilityResponse = {}
			const planDetails = {
				month: String(comparisonPrice.value.month),
				product_id: id
			}
			const query = { eligibility: true }
			if (gradeStatus.value === 'upgrade') {
				const res = await computePlanUpgrade(
					props.planId as string,
					planDetails,
					'plan-upgrade-summary',
					planCategory.value,
					query
				)
				if (res?.data) {
					result = res.data as EligibilityResponse
				}
			} else if (gradeStatus.value === 'downgrade') {
				const res = await computePlanDowngrade(
					props.planId as string,
					planDetails,
					'plan-downgrade-summary',
					planCategory.value,
					query
				)
				if (res?.data) {
					result = res.data as EligibilityResponse
				}
			}
			// This moves to the next page if the object keys are empty
			if (result && !Object.keys(result).length && !categoryType.value.isHealth) {
				return await navigateToMigration(id)
			}
			eligibilityDialogRef.value?.checkEligibility(result, gradeStatus.value)
			comparePlanId.value = id
		}, computingGrades)
	}

	const fetchProductInfo = useDebounceFn(async () => {
		resolve(async () => {
			if (planInfo.value?.product?.id) {
				if (categoryType.value.isHealth && isMigratingPolicy.value) {
					await updateHasData(props.planId)
					migrationDialog.value = !hasPolicyToMigrate.value
				}
				// Prevent from fetching products for non-auto and non-health plans
				if (!categoryType.value?.isAuto && !categoryType.value.isHealth) {
					return null
				}
				if (groupedProduct.value || secondaryPlanList.value.length > 0) {
					return null
				}
				const { user } = useUser()
				// This will toggle the pattern at which the product will be grouped or ungrouped
				const exclude = categoryType.value?.isAuto ? 'groupedByName' : 'productList'
				const planListResponse = await getProductList<GroupProductByNameContext | ProductContext>({
					category: planCategory.value,
					excludes: [exclude]
					// entity_id: user?.customer?.id ?? ''
				})
				// Since we can have both a list and an object record let's build the return format for our tab
				const tabTransform = (arg: Array<string>): lineTabContext[] => {
					return arg
						.map(item => {
							return {
								key: toLower(item),
								name: toLower(item).replace('care', 'Care')
							}
						})
						.filter(item => item.key !== 'health')
				}
				if (planListResponse) {
					if (!Array.isArray(planListResponse)) {
						groupedProduct.value = planListResponse
					} else {
						secondaryPlanList.value = pillTabTransform(planListResponse)
					}
					if (categoryType.value.isHealth) {
						primaryPlanList.value = tabTransform(Object.keys(planListResponse as GroupProductByNameContext))
					} else {
						primaryPlanList.value = tabTransform(
							(planListResponse as ProductContext).map(item => item.name)
						)
					}
				}
				// Select the current plan by default
				activeTab.value = toLower(currentPlanName.value)

				// migrationDisabled.value = daysLeftForMigration.value >= 1

				resolve(async () => {
					if (planInfo.value?.details?.month) {
						// Get current plan price
						const planPrice = await getProductPriceByMonth(
							planInfo.value.product.id || '',
							planInfo.value.details.month
						)
						if (planPrice?.data) {
							currentPlanPriceDetails.value = {
								...planPrice.data,
								genius_price: !planPrice?.data.is_dynamic_price
									? planPrice.data.genius_price
									: Math.round(planPrice.data.genius_price)
							}
						}
					}
				}, loadingComparison)
			}
		}, loadingComparison)
	}, 100)

	watch(activeTab, current => {
		const key = toLower(current)
		// Since we have grouped product we need to check if the product exists in the grouped product
		if (groupedProduct.value) {
			let list: (lineTabContext & ProductContext[number])[] = []
			if (groupedProduct.value[key]) {
				list = pillTabTransform(groupedProduct.value[key].list || [])
			} else {
				list = pillTabTransform(Object.values(groupedProduct.value)[0].list || [])
			}

			secondaryPlanList.value = list
			secondaryPlanSelected.value =
				secondaryPlanList.value.find(item => item.code_name === planInfo.value?.product?.code_name)
					?.code_name ||
				secondaryPlanList.value[0].code_name ||
				0
		}
	})
	watch(
		() => planInfo.value,
		() => fetchProductInfo(),
		{ immediate: true }
	)
	// Track product switch
	watch(productHighlight, update => {
		if (update?.id) {
			getProductPrice(update?.id || '', update)
		}
	})
</script>

<template>
	<div class="">
		<template v-if="categoryType.isHealth || categoryType?.isAuto">
			<tabs-line-tab
				v-model:active-tab="activeTab"
				:tabs="primaryPlanList"
				:ui="{
					tab: {
						width: 'w-full',
						default: 'gap-[8px]',
						padding: 'px-5 md:py-[30px] py-7',
						inactive: { font: 'font-medium', color: 'text-gray-500' },
						active: { color: 'text-blue-950', font: 'font-semibold' },
						body: {
							default: 'tabs-panel whitespace-nowrap flex w-full ',
							margin: '',
							padding: '',
							border: ' border-gray-200 lg:border-b-[4px]',
							overflowX: 'overflow-visible'
						},
						wrapper: {
							default: 'w-full items-center  overflow-x-auto border lg:border-b-0',
							display: 'flex justify-evenly'
						}
					},
					line: {
						default: 'bg-main lg:translate-y-[3.5px]',
						height: 'h-[3px]',
						border: 'border-0 rounded-lg'
					}
				}"
			>
				<template #tab="{ item }">
					<lazy-tools-icon
						:icon="addProductsIconFill(planCategory, String(item?.key ?? '')).name"
						:dir="addProductsIconFill(planCategory, String(item?.key ?? '')).dir"
						:path-class="addProductsIconFill(planCategory, String(item?.key ?? '')).pathClass"
						:width="16.831"
						:height="15.429"
					/>
					<span>{{ item?.name }}</span>
				</template>
			</tabs-line-tab>

			<div class="min-h-[400px] w-full border border-t-0 py-5 sm:px-8">
				<tabs-pill
					v-if="categoryType.isHealth && secondaryPlanList.length > 0"
					v-model:active-tab="secondaryPlanSelected"
					:tabs="secondaryPlanList"
					:loading="loadingComparison"
					:ui="{
						wrapper: {
							width: '',
							margin: 'mx-auto',
							default: 'items-center overflow-x-auto flex gap-[10px] py-[8px] px-[10px]',
							bg: 'bg-gray-100',
							rounded: 'rounded-[40px]',
							alignItems: 'justify-evenly'
						},
						tab: {
							default: 'text-gray-400  bg-gray-100 capitalize ',
							paddingInline: 'px-[10px] py-[2px]',
							rounded: 'rounded-[20px]',
							base: '',
							width: '',
							active: {
								color: 'bg-white text-gray-600',
								font: 'font-semibold'
							},
							inactive: {
								color: 'text-gray-600',
								font: 'font-normal'
							}
						}
					}"
				/>

				<div
					class="w-full rounded-lg border border-purple-100 bg-purple-50 px-5 py-5 text-sm font-normal leading-6 tracking-wide text-gray-600"
				>
					<span class="font-bold capitalize">{{ productHighlight?.name ?? '' }}</span>

					<span v-show="categoryType?.isAuto">
						plan protects every vehicle in your business from all kinds of damages at
						<span class="font-medium text-gray-600">
							{{
								!computingGrades
									? comparisonPrice.is_dynamic_price
										? (comparisonPrice.genius_price || currentPlanPriceDetails.market_price) + '%'
										: currencyToNaira(
												comparisonPrice.genius_price ||
													Number(currentPlanPriceDetails.market_price)
											)
									: 'loading price...'
							}}
							of vehicle value
						</span>
						per vehicle yearly.
					</span>

					<span v-show="categoryType.isHealth">
						plan provides you with access to quality Healthcare at
						{{
							!computingGrades
								? currencyToNaira(
										(comparisonPrice.genius_price ||
											Number(currentPlanPriceDetails.market_price ?? 0)) *
											Number(comparisonPrice.month)
									)
								: 'loading price...'
						}}
						per staff {{ comparisonPrice?.cover_period ?? 'N/A' }}.
					</span>
				</div>

				<products-benefits
					:view-all="false"
					:default-btn="false"
					:loading="computingGrades"
					:product="productHighlight ?? null"
					class="mt-8 min-h-[300px]"
				/>

				<button
					v-if="productHighlight?.code_name === planDetails?.product?.code_name"
					class="default-btn mt-12 w-full justify-center bg-gray-200 text-gray-500"
					disabled
				>
					Current Plan
				</button>

				<a-button
					v-else-if="productHighlight?.id && comparisonPrice"
					:loading="computingGrades"
					:name="gradeStatus"
					data-testid="migrate-plan"
					:disabled="computingGrades || migrationDisabled"
					class="default-btn mt-12 w-full justify-center gap-x-3 space-x-1 !bg-primary-2 !font-medium !text-white disabled:!bg-primary-300"
					@click="() => confirmMigration()"
				>
					<span v-show="computingGrades"> Computing Grades... </span>
					<span v-show="!computingGrades">
						<span class="capitalize">{{ gradeStatus }} </span>

						{{ ` ${hasPolicyToMigrate ? ` ${selectedPolicyToMigrate} staff` : 'all staff'} to this Plan` }}
					</span>
				</a-button>
			</div>

			<my-cover-migration-eligibility-dialog
				ref="eligibilityDialogRef"
				v-model="eligibilityDialog"
				v-model:type="eligibilityDialogType"
				:plan-id="planId"
				:from="planDetails?.product?.name ?? ''"
				:to="productHighlight?.name ?? ''"
				@adjust-plan="confirmMigration"
			/>
		</template>
	</div>
</template>

<style scoped>
	.wrapper-shadow {
		box-shadow:
			0.5px 0.75px 1.5px 0px #00000011,
			0px -0.25px 1.5px 0px #00000014;
	}
</style>
